import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@coreui/angular-pro';
import { TranslatePipe } from 'i18n';

import { CompanionGoal, CompanionOverview, CompanionProfile, HealthCompanionService } from '../../../services/health-companion.service';

type Step = 'age' | 'goals' | 'baseline' | 'time' | 'movement' | 'restrictions' | 'settings' | 'review';
const STEPS: Step[] = ['age', 'goals', 'baseline', 'time', 'movement', 'restrictions', 'settings', 'review'];
const GOAL_KINDS = ['weight', 'muscle', 'energy', 'sleep', 'blood_pressure', 'fitness', 'nutrition', 'custom'];
const COMMON_ZONES = ['Europe/Vienna', 'Europe/Berlin', 'Europe/Zurich', 'Europe/London', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Prague', 'Europe/Bratislava', 'Europe/Lisbon', 'Europe/Moscow', 'America/New_York', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles', 'America/Sao_Paulo', 'America/Mexico_City', 'Asia/Dubai', 'Asia/Bangkok',
  'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'Africa/Johannesburg', 'UTC'];

/**
 * The "Profil" tab (plan §4.2 + §13.1): the getting-to-know dialogue as steps
 * — age check first (full date of birth, explicitly confirmed; under 18 locks
 * the companion until a guardian consents), then goals, starting point, time,
 * movement options, restrictions, settings, and the correction view. Each step
 * saves at once (interruptible). A confirmed profile shows the same fields as
 * one editable form.
 */
@Component({
  selector: 'app-companion-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, ButtonDirective],
  templateUrl: './companion-profile.component.html',
  styles: [`
    .steps { display: flex; flex-wrap: wrap; gap: .3rem; margin-bottom: .75rem; }
    .steps span { background: var(--gh-sage-soft, #f1f5ef); border-radius: 999px; font-size: .75rem; padding: .15rem .55rem; }
    .steps span.on { background: var(--gh-green-action, #0E8F5E); color: #fff; }
    .goal { align-items: center; display: grid; gap: .4rem; grid-template-columns: 11rem 1fr auto; margin-bottom: .4rem; }
    .grid2 { display: grid; gap: .6rem; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
    @media (max-width: 575.98px) { .goal { grid-template-columns: 1fr; } }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanionProfileComponent {
  private companion  = inject(HealthCompanionService);
  private destroyRef = inject(DestroyRef);

  overview = input.required<CompanionOverview>();
  readOnly = input(false);
  lang     = input('en');
  changed  = output<void>();

  readonly steps = STEPS;
  readonly goalKinds = GOAL_KINDS;
  readonly zones = COMMON_ZONES;

  busy  = signal(false);
  error = signal<string | null>(null);
  saved = signal(false);
  step  = signal<Step>('age');

  // Age check
  dob = signal('');
  dobConfirm = signal(false);

  // Form state (mirrors the profile)
  goals = signal<CompanionGoal[]>([]);
  baselineText = signal('');
  dailyMinutes = signal<string>('');
  weekdayMinutes = signal<string>('');
  movement = signal('');
  restrictions = signal('');
  language = signal('de');
  timeZone = signal('UTC');
  weightUnit = signal<'kg' | 'lb'>('kg');
  volumeUnit = signal<'ml' | 'l' | 'fl_oz'>('ml');
  height = signal('');

  // Guardian
  gName = signal('');
  gEmail = signal('');
  gRelation = signal('mother');

  profile = computed(() => this.overview().profile);
  minorLocked = computed(() => this.overview().entitlement.status === 'minor_consent_required');
  confirmed = computed(() => this.profile()?.confirmed === true);
  browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  constructor() {
    // untracked: hydrate reads and writes the form signals; tracked, every
    // write (e.g. the prepared first goal) would re-run the effect for ever.
    effect(() => {
      const p = this.profile();
      untracked(() => this.hydrate(p));
    }, { allowSignalWrites: true });
  }

  private hydrate(p: CompanionProfile | null): void {
    if (!p) {
      this.timeZone.set(this.browserZone);
      this.language.set(this.lang() === 'de' ? 'de' : 'en');
      this.step.set('age');
      return;
    }
    const goals = p.goals?.length ? p.goals.map(g => ({ ...g })) : [];
    // The first goal field is already there — the member types, instead of
    // looking for an "add" link (staging feedback 2026-11-04).
    if (!p.confirmed && goals.length === 0) goals.push(this.newGoal(p));
    this.goals.set(goals);
    this.baselineText.set((p.baseline as { text?: string } | null)?.text ?? '');
    this.dailyMinutes.set(p.daily_time_minutes == null ? '' : String(p.daily_time_minutes));
    this.weekdayMinutes.set(p.weekday_time_minutes == null ? '' : String(p.weekday_time_minutes));
    this.movement.set(p.movement_options ?? '');
    this.restrictions.set(p.restrictions ?? '');
    this.language.set(p.language || 'en');
    this.timeZone.set(p.time_zone || this.browserZone);
    this.weightUnit.set(p.weight_unit);
    this.volumeUnit.set(p.volume_unit);
    this.height.set(p.height_cm == null ? '' : String(p.height_cm));
    if (!p.age_verified) this.step.set('age');
    else if (p.confirmed) this.step.set('review');
    else this.step.set(STEPS.includes(p.onboarding_step as Step) ? p.onboarding_step as Step : 'goals');
  }

  /** 1-based position of the current step for "Schritt n von N". */
  stepNumber(): number { return STEPS.indexOf(this.step()) + 1; }
  stepTotal(): number { return STEPS.length; }
  hasGoal(): boolean { return this.goals().some(g => g.text.trim().length > 0); }
  canContinue(): boolean { return this.step() !== 'goals' || this.hasGoal(); }

  // ----- age -----------------------------------------------------------------------

  submitAge(): void {
    if (!this.dob() || !this.dobConfirm() || this.busy()) return;
    this.busy.set(true);
    this.error.set(null);
    this.companion.ageCheck(this.dob(), this.timeZone() || this.browserZone, this.language())
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { this.busy.set(false); this.changed.emit(); },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error ?? 'error'); }
      });
  }

  // ----- guardian -----------------------------------------------------------------

  requestGuardian(): void {
    if (this.busy() || !this.gName().trim() || !this.gEmail().trim()) return;
    this.busy.set(true);
    this.error.set(null);
    this.companion.requestGuardian(this.gName().trim(), this.gEmail().trim(), this.gRelation())
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { this.busy.set(false); this.gName.set(''); this.gEmail.set(''); this.changed.emit(); },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error ?? 'error'); }
      });
  }

  revokeGuardian(): void {
    this.companion.revokeGuardian().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.changed.emit(), error: () => this.error.set('error')
    });
  }

  // ----- goals --------------------------------------------------------------------------

  private newGoal(p: CompanionProfile | null): CompanionGoal { return { kind: p?.minor ? 'energy' : 'weight', text: '' }; }
  addGoal(): void { this.goals.update(g => [...g, this.newGoal(this.profile())]); }
  removeGoal(i: number): void { this.goals.update(g => g.filter((_, idx) => idx !== i)); }
  setGoal(i: number, patch: Partial<CompanionGoal>): void { this.goals.update(g => g.map((x, idx) => idx === i ? { ...x, ...patch } : x)); }
  allowedGoalKinds(): string[] { return this.profile()?.minor ? GOAL_KINDS.filter(k => k !== 'weight') : GOAL_KINDS; }

  // ----- steps / save ---------------------------------------------------------------------

  private payload(): Partial<CompanionProfile> {
    return {
      goals: this.goals().filter(g => g.text.trim()).map(g => ({ kind: g.kind, text: g.text.trim() })),
      baseline: this.baselineText().trim() ? { text: this.baselineText().trim() } : {},
      daily_time_minutes: this.dailyMinutes() === '' ? null : Number(this.dailyMinutes()),
      weekday_time_minutes: this.weekdayMinutes() === '' ? null : Number(this.weekdayMinutes()),
      movement_options: this.movement().trim() || null,
      restrictions: this.restrictions().trim() || null,
      language: this.language(), time_zone: this.timeZone(),
      weight_unit: this.weightUnit(), volume_unit: this.volumeUnit(),
      height_cm: this.height() === '' ? null : this.height()
    };
  }

  next(): void { this.save(STEPS[Math.min(STEPS.indexOf(this.step()) + 1, STEPS.length - 1)]); }
  back(): void { this.step.set(STEPS[Math.max(STEPS.indexOf(this.step()) - 1, 1)]); }

  save(nextStep?: Step): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.error.set(null);
    this.saved.set(false);
    const stepToStore = this.confirmed() ? undefined : (nextStep ?? this.step());
    this.companion.updateProfile(this.payload(), stepToStore).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busy.set(false);
        this.saved.set(true);
        if (nextStep) this.step.set(nextStep);
        this.changed.emit();
      },
      error: err => { this.busy.set(false); this.error.set(err?.error?.error === 'invalid' ? 'profile_invalid' : (err?.error?.error ?? 'error')); }
    });
  }

  confirm(): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.companion.updateProfile(this.payload(), 'review').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.companion.confirmProfile().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { this.busy.set(false); this.changed.emit(); },
        error: err => { this.busy.set(false); this.error.set(err?.error?.error ?? 'error'); }
      }),
      error: err => { this.busy.set(false); this.error.set(err?.error?.error === 'invalid' ? 'profile_invalid' : 'error'); }
    });
  }
}
